import {
  DataLoadingWrapper,
  InteractiveContainer,
  PlainButton,
} from "@jugl-web/ui-components";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import {
  useRestApiProvider,
  type AgentConfigDto,
  type AgentTool,
  type CustomTemplateData,
} from "@jugl-web/rest-api";
import { useAppVariant, useToast, useTranslations } from "@jugl-web/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { CXAutomationDrawer } from "@web-src/modules/cx/pages/CXActionsPage/components/CXAutomationDrawer/CXAutomationDrawer";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as CreateNewIcon } from "./assets/create-new.svg";
import { CustomToolCard } from "./components/CustomToolCard";

type DialogState =
  | { isOpen: false }
  | { isOpen: true; editingIndex: number | null };

const getCustomAutomations = (config: AgentConfigDto | undefined) => {
  const apiData = config?.config.tools?.find(
    (tool) => tool.pack === "custom"
  )?.custom_template_data;
  return apiData && Array.isArray(apiData) ? apiData : [];
};

export const CustomSection: React.FC<{ entityId: string }> = ({ entityId }) => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const showAlert = useShowAlert();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { integrationsApi } = useRestApiProvider();

  const {
    data: agentConfig,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [updateAgentConfig, { isLoading: isUpdating }] =
    integrationsApi.useUpdateAgentConfigMutation();

  const automationsFromApi = useMemo(
    () => getCustomAutomations(agentConfig),
    [agentConfig]
  );

  const [dialog, setDialog] = useState<DialogState>({ isOpen: false });
  const [automations, setAutomations] = useState<CustomTemplateData[]>([]);
  const [savingIndices, setSavingIndices] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  const [pendingSaves, setPendingSaves] = useState(0);

  const agentConfigRef = useRef(agentConfig);

  useEffect(() => {
    agentConfigRef.current = agentConfig;
  }, [agentConfig]);

  // While a save is in flight (or the refetch it triggers hasn't landed), keep
  // showing the optimistic local list. Once the server catches up, resync so
  // local state never silently diverges from the canonical config.
  useEffect(() => {
    if (pendingSaves > 0 || isFetching) return;
    setAutomations(automationsFromApi);
  }, [automationsFromApi, pendingSaves, isFetching]);

  const persistAutomations = useCallback(
    async (next: CustomTemplateData[]) => {
      const config = agentConfigRef.current;
      if (!config) {
        toast(
          t({
            id: "cx-actions-page.configure-agent-first",
            defaultMessage: "Set your Business Name in Setup & Test first",
          }),
          { variant: "error" }
        );
        return false;
      }

      const customTool: AgentTool | null = next.length
        ? { pack: "custom", tools: null, custom_template_data: next }
        : null;
      const nextTools: AgentTool[] = [
        ...config.config.tools.filter((tool) => tool.pack !== "custom"),
        ...(customTool ? [customTool] : []),
      ];
      const nextConfig = { ...config.config, tools: nextTools };
      const previousConfig = config;

      agentConfigRef.current = { ...config, config: nextConfig };

      try {
        await updateAgentConfig({
          entityId,
          config: nextConfig,
        }).unwrap();
        return true;
      } catch {
        agentConfigRef.current = previousConfig;
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
        return false;
      }
    },
    [entityId, toast, updateAgentConfig, t]
  );

  const saveAutomations = useCallback(
    async (next: CustomTemplateData[]) => {
      setPendingSaves((count) => count + 1);
      setAutomations(next);
      const ok = await persistAutomations(next);
      setPendingSaves((count) => count - 1);
      return ok;
    },
    [persistAutomations]
  );

  const handleSave = useCallback(
    async (data: CustomTemplateData) => {
      if (!dialog.isOpen) return;
      const { editingIndex } = dialog;

      const next =
        editingIndex === null
          ? [...automations, data]
          : automations.map((item, idx) =>
              idx === editingIndex ? data : item
            );

      const ok = await saveAutomations(next);
      if (ok) setDialog({ isOpen: false });
    },
    [automations, dialog, saveAutomations]
  );

  const handleToggleEnabled = useCallback(
    async (idx: number, isEnabled: boolean) => {
      const next = automations.map((item, i) =>
        i === idx ? { ...item, enabled: isEnabled } : item
      );
      setSavingIndices((prev) => new Set(prev).add(idx));

      await saveAutomations(next);

      setSavingIndices((prev) => {
        const updated = new Set(prev);
        updated.delete(idx);
        return updated;
      });
    },
    [automations, saveAutomations]
  );

  const handleDelete = useCallback(
    (idx: number) => {
      showAlert({
        title: t({
          id: "cx-actions-page.delete-custom-tool",
          defaultMessage: "Delete this automation?",
        }),
        content: t({
          id: "cx-actions-page.delete-custom-tool-body",
          defaultMessage: "This will remove the automation from your AI Agent.",
        }),
        buttons: [
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            color: "grey",
            role: "close",
          },
          {
            text: t({ id: "common.delete", defaultMessage: "Delete" }),
            color: "tertiary",
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await saveAutomations(
                automations.filter((_item, i) => i !== idx)
              );
            },
          },
        ],
      });
    },
    [automations, saveAutomations, showAlert, t]
  );

  const openCreateDialog = () => {
    if (isAnonymous) {
      cxSignupDialog?.open();
      return;
    }
    if (!agentConfig) {
      toast(
        t({
          id: "cx-actions-page.configure-agent-first",
          defaultMessage: "Set your Business Name in Setup & Test first",
        }),
        { variant: "error" }
      );
      return;
    }
    setDialog({ isOpen: true, editingIndex: null });
  };

  return (
    <DataLoadingWrapper
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
    >
      <div className="flex flex-wrap gap-8">
        {automations.map((automation, idx) => (
          <CustomToolCard
            key={`${automation.name ?? "tool"}-${+idx}`}
            tool={automation}
            onEdit={() => setDialog({ isOpen: true, editingIndex: idx })}
            onDelete={() => handleDelete(idx)}
            onToggleEnabled={(isEnabled) => handleToggleEnabled(idx, isEnabled)}
            isToggleDisabled={savingIndices.has(idx)}
          />
        ))}
        <InteractiveContainer
          onClick={openCreateDialog}
          className="flex w-full max-w-[480px] min-w-[440px] items-stretch gap-4 rounded-xl border border-solid border-dark-100 bg-white p-5 transition-colors hover:border-dashed hover:border-primary-200"
        >
          <div
            className="flex h-[136px] w-[100px] shrink-0 items-center justify-center rounded-xl"
            style={{
              backgroundImage:
                "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
              backgroundSize: "8px 8px",
            }}
          >
            <CreateNewIcon />
          </div>
          <div className="flex flex-col justify-between">
            <div className="flex flex-col gap-1.5">
              <span className="font-secondary text-lg leading-[27px] font-semibold text-black">
                {t({
                  id: "cx-actions-page.create-new",
                  defaultMessage: "Create New",
                })}
              </span>
              <span className="font-secondary text-sm leading-[21px] text-[#737B7E]">
                {t({
                  id: "cx-actions-page.tap-to-create-new-automation",
                  defaultMessage: "Tap to create new automation",
                })}
              </span>
            </div>
            <PlainButton
              onClick={(event) => {
                event.stopPropagation();
                openCreateDialog();
              }}
              className="mt-2 flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-dark-100 bg-white"
            >
              <AddIcon />
            </PlainButton>
          </div>
        </InteractiveContainer>
      </div>
      {dialog.isOpen && (
        <CXAutomationDrawer
          isOpen
          onRequestClose={() => {
            if (!isUpdating) setDialog({ isOpen: false });
          }}
          entityId={entityId}
          onSave={handleSave}
          isSaving={isUpdating}
          initialAutomation={
            dialog.editingIndex !== null
              ? automations[dialog.editingIndex]
              : undefined
          }
        />
      )}
    </DataLoadingWrapper>
  );
};
